<template>
    <div class="shot-details">
        <div class="shot-details__hint">
            All optional: tap what you know, the rest stays empty. Tap again to unselect.
        </div>

        <section v-for="group in groups" :key="group.key" class="shot-details__group">
            <div class="shot-details__head">
                <span class="shot-details__title">{{ group.title }}</span>
                <span class="shot-details__subtitle">{{ group.subtitle }}</span>
                <v-btn
                    icon
                    x-small
                    class="shot-details__info"
                    :title="`What each ${group.title.toLowerCase()} means`"
                    @click="toggleInfo(group.key)"
                >
                    <v-icon small :color="openInfo === group.key ? 'primary' : 'grey'">mdi-help-circle-outline</v-icon>
                </v-btn>
            </div>

            <!-- the definitions, so every operator picks the same option for the same play -->
            <div v-if="openInfo === group.key" class="shot-details__definitions">
                <div v-for="option in group.options" :key="option.value" class="shot-details__definition">
                    <b>{{ option.label }}</b><span v-if="option.hint"> ({{ option.hint }})</span>: {{ option.definition }}
                </div>
            </div>

            <div class="shot-details__options">
                <button
                    v-for="option in group.options"
                    :key="option.value"
                    type="button"
                    class="shot-details__option"
                    :class="{ 'shot-details__option--active': details[group.key] === option.value }"
                    :style="details[group.key] === option.value ? activeStyle : null"
                    :title="option.definition"
                    @click="pick(group.key, option.value)"
                >
                    <span class="shot-details__label">{{ option.label }}</span>
                    <span v-if="option.hint" class="shot-details__option-hint">{{ option.hint }}</span>
                </button>
            </div>
        </section>

        <div class="shot-details__actions">
            <v-btn v-if="editing" text large class="sd-btn" @click="clearAll">
                <v-icon left>mdi-eraser</v-icon>Clear all
            </v-btn>
            <v-spacer />
            <v-btn outlined large class="sd-btn" @click="$emit('skip')">
                {{ editing ? 'Cancel' : 'Skip' }}
            </v-btn>
            <v-btn depressed large color="black" class="white--text sd-btn" @click="$emit('save', { ...details })">
                <v-icon left>mdi-check</v-icon>Save
            </v-btn>
        </div>
    </div>
</template>

<script>
    import { SHOT_DETAIL_GROUPS, emptyShotDetails } from '@/components/stats/shotDetails'

    // Advanced stats of one shot: situation, play type, role, shot type (see shotDetails.js)
    export default {
        name: 'StatsShotDetails',
        props: {
            // the values to start from (suggested for a new shot, the saved ones when editing)
            value: {
                type: Object,
                default: null
            },
            // editing a shot of the feed: Cancel instead of Skip, and Clear all
            editing: {
                type: Boolean,
                default: false
            },
            color: {
                type: String,
                default: '#009688'
            },
            textColor: {
                type: String,
                default: '#ffffff'
            }
        },
        data() {
            return {
                groups: SHOT_DETAIL_GROUPS,
                details: { ...emptyShotDetails(), ...(this.value || {}) },
                openInfo: null
            }
        },
        computed: {
            activeStyle() {
                return { backgroundColor: this.color, borderColor: this.color, color: this.textColor }
            }
        },
        methods: {
            pick(key, value) {
                this.details[key] = this.details[key] === value ? null : value
            },
            toggleInfo(key) {
                this.openInfo = this.openInfo === key ? null : key
            },
            clearAll() {
                this.details = emptyShotDetails()
            }
        }
    }
</script>

<style scoped>
.shot-details {
    padding: 8px 14px 16px;
}

.shot-details__hint {
    margin-bottom: 8px;
    color: rgba(0, 0, 0, 0.55);
    font-size: 12px;
}

.shot-details__group {
    margin-bottom: 14px;
}

.shot-details__head {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 6px;
}

.shot-details__title {
    font-size: 14px;
    font-weight: 800;
}

.shot-details__subtitle {
    color: rgba(0, 0, 0, 0.5);
    font-size: 12px;
}

.shot-details__info {
    align-self: center;
}

.shot-details__definitions {
    margin-bottom: 8px;
    padding: 8px 10px;
    border-radius: 8px;
    background: #fff8e1;
    font-size: 12px;
    line-height: 1.45;
}

.shot-details__definition + .shot-details__definition {
    margin-top: 4px;
}

.shot-details__options {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 6px;
}

.shot-details__option {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    min-height: 46px;
    padding: 6px 10px;
    border: 1px solid rgba(0, 0, 0, 0.18);
    border-radius: 10px;
    background: #fff;
    text-align: left;
    line-height: 1.2;
}

.shot-details__option:active {
    background: #f2f2f2;
}

.shot-details__label {
    font-size: 13px;
    font-weight: 800;
}

.shot-details__option-hint {
    font-size: 11px;
    opacity: 0.7;
}

.shot-details__actions {
    position: sticky;
    bottom: 0;
    display: flex;
    gap: 8px;
    padding-top: 10px;
    background: #fff;
}
</style>
